import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from "reactstrap";
import { AppContext } from '../application/provider';
import GestionCorreoService from "../axios_services/gestionCorreo.service";
import Loader from "../components/Loader/Loader";
import { ButtonSearch, ButtonInsert, ButtonUpdate, ButtonDelete } from '../components/Buttons/Buttons';
import ModalGestionCorreo from '../components/Modal/ModalGestionCorreo';
import './GestionCorreo.css';

function GestionCorreo() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const initialFetchDone = useRef(false);

    const [tiposSolicitud] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 40));
    const [estadosGestion] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 41));
    const [usuariosCatalog] = useState(state.user?.personal || []);

    const [periodo, setPeriodo] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [estadoFiltro, setEstadoFiltro] = useState('');

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadInitialPageData();
            initialFetchDone.current = true;
        }
    }, []);

    const loadInitialPageData = async () => {
        setIsLoading(true);
        try {
            const response = await GestionCorreoService.getGestionCorreo(periodo, estadoFiltro);
            setRegistros(response.data || []);
        } catch (error) {
            console.error(error);
            setRegistros([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = async () => {
        await loadInitialPageData();
    };

    const handleNuevoRegistro = () => {
        setSelectedRegistro(null);
        setIsModalOpen(true);
    };

    const handleEditarRegistro = (registro) => {
        setSelectedRegistro(registro);
        setIsModalOpen(true);
    };

    const handleCloseModal = (shouldReload = false) => {
        setIsModalOpen(false);
        setSelectedRegistro(null);
        if (shouldReload === true) {
            loadInitialPageData();
        }
    };

    const handleSaveRegistro = async (registroData) => {
        setIsLoading(true);
        setIsModalOpen(false);
        try {
            let response;
            if (registroData.gestion_correo_id) {
                response = await GestionCorreoService.updateGestionCorreo(registroData);
            } else {
                response = await GestionCorreoService.insertGestionCorreo(registroData);
            }
            await loadInitialPageData();
            return response.data || response;
        } catch (error) {
            setIsLoading(false);
            alert(error.response?.data?.message || 'Ocurrió un error al guardar.');
            throw error;
        }
    };

    const handleEliminarRegistro = async (registro) => {
        if (registro.estado == 2) {
            alert('No se puede eliminar un registro en estado "Atendido". Solo se pueden eliminar registros en estado "Pendiente"');
            return;
        }

        if (!window.confirm('¿Está seguro de eliminar este registro?')) return;
        setIsLoading(true);
        try {
            await GestionCorreoService.deleteGestionCorreo({ gestion_correo_id: registro.gestion_correo_id });
            await loadInitialPageData();
        } catch (error) {
            alert(error.response?.data?.message || 'Ocurrió un error al eliminar.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        return fecha.split('T')[0];
    };

    const getEstadoLabel = (estado) => {
        if (estado == 1) return <span style={{ color: '#3cb167', fontWeight: 'bold' }}>Activo</span>;
        if (estado == 2) return <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>Pendiente</span>;
        return <span style={{ color: '#f87171' }}>Inactivo</span>;
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="caja-flujo-container">
            <h1>GESTIÓN DE CORREO</h1>

            <div className="filtros">
                <div className="campo-fecha">
                    <label>Periodo:</label>
                    <input
                        type="month"
                        value={periodo}
                        onChange={(e) => setPeriodo(e.target.value)}
                    />
                </div>

                <div className="campo-fecha">
                    <label>Estado:</label>
                    <select
                        value={estadoFiltro}
                        onChange={(e) => setEstadoFiltro(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todos los estados</option>
                        {estadosGestion?.map((estado) => (
                            <option key={estado.tipo_id} value={estado.tipo_id}>
                                {estado.descripcion}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <ButtonSearch
                onClick={handleSearch}
                isLoading={isLoading}
            />

            <div className="nuevo-movimiento-section">
                {[8, 5, 22, 12, 24, 25, 26].includes(Number(state.user?.perfil_id)) && (
                    <ButtonInsert onClick={handleNuevoRegistro} />
                )}
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>Solicitante</th>
                            <th>Fecha Registro</th>
                            <th>Tipo Solicitud</th>
                            <th>Cargo</th>
                            <th>Colaborador</th>
                            <th>Correo</th>
                            <th>Fecha Atención</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registros.length > 0 ? (
                            registros.map((registro) => {
                                const usuarioSolicitante = state.user?.personal?.find(u => u.usuario_id == registro.usuario_id_solicitante);
                                const usuarioCorreo = state.user?.personal?.find(u => u.usuario_id == registro.usuario_id_correo);
                                const tipoSolicitudLabel = state.catalogos?.tipos?.find(t => t.tipo_id == registro.tipo_solicitud && t.categoria_id == 40)?.descripcion;
                                const estadoLabel = state.catalogos?.tipos?.find(t => t.tipo_id == registro.estado && t.categoria_id == 41)?.descripcion;
                                const perfilCorreoLabel = state.catalogos?.perfiles?.find(p => p.perfil_id == registro.perfil_id_correo)?.descripcion;

                                const getRowStyle = (estado) => {
                                    if (estado == 2) return { backgroundColor: '#fef3c7' }; // Amarillo suave (En Proceso)
                                    if (estado == 3) return { backgroundColor: '#dcfce7' }; // Verde suave (Finalizado)
                                    return {};
                                };
                                const rowStyle = getRowStyle(registro.estado);

                                return (
                                    <tr key={registro.gestion_correo_id} style={rowStyle}>
                                        <td>{usuarioSolicitante ? usuarioSolicitante.nombre_completo_usuario.toUpperCase() : String(registro.usuario_id_solicitante).toUpperCase()}</td>
                                        <td>{formatFecha(registro.fecha_registro)}</td>
                                        <td>{tipoSolicitudLabel || registro.tipo_solicitud}</td>
                                        <td>{perfilCorreoLabel || registro.perfil_id_correo || '-'}</td>
                                        <td>{usuarioCorreo ? usuarioCorreo.nombre_completo_usuario.toUpperCase() : String(registro.usuario_id_correo).toUpperCase()}</td>
                                        <td>{registro.correo_corporativo || '-'}</td>
                                        <td>{formatFecha(registro.fecha_atencion)}</td>
                                        <td>{estadoLabel || registro.estado}</td>
                                        <td>
                                            {[8, 5, 22, 12, 24, 25, 26].includes(Number(state.user?.perfil_id)) && (
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                    <ButtonUpdate onClick={() => handleEditarRegistro(registro)} />
                                                    <ButtonDelete onClick={() => handleEliminarRegistro(registro)} />
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={9} className="text-center text-gray-500 py-4">
                                    No hay registros disponibles.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalGestionCorreo
                isOpen={isModalOpen}
                registro={selectedRegistro}
                onSave={handleSaveRegistro}
                onClose={handleCloseModal}
                usuarioId={state.user?.usuario_id}
                perfilId={state.user?.perfil_id}
                tiposSolicitud={tiposSolicitud}
                estadosGestion={estadosGestion}
                usuariosCatalog={usuariosCatalog}
                perfilesCatalog={state.catalogos?.perfiles || []}
            />
        </div>
    );
}

export default GestionCorreo;